/**
 * Email Delivery — did the email actually arrive?
 *
 * Everything the app sends leaves as noreply@tricorps.com, which nobody
 * watches, so a bounce used to be invisible. Resend now posts delivery events
 * back to /api/webhooks/resend and they land here. Failures are what this
 * screen is for — the default filter is "Problems only".
 */
import React, { useEffect, useState } from 'react';
import { emailDeliveryAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MailWarning, RefreshCw, Search, TriangleAlert, Send } from 'lucide-react';
import { useCap } from '../components/auth/RequireCap';
import AccessState from '../components/auth/AccessState';

const TONE = {
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  opened: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  clicked: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sent: 'bg-slate-50 text-slate-600 border-slate-200',
  delayed: 'bg-amber-50 text-amber-700 border-amber-200',
  bounced: 'bg-red-50 text-red-700 border-red-200',
  complained: 'bg-red-50 text-red-700 border-red-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  failed_at_send: 'bg-red-50 text-red-700 border-red-200',
};

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export default function EmailDelivery() {
  const canView = useCap('admin', 'view');
  const canEdit = useCap('admin', 'edit');
  const [status, setStatus] = useState('failed');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ messages: [], counts: {}, failed_count: 0 });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const sendTest = async () => {
    setTesting(true);
    try {
      // Resend's sandbox address: always delivers, never touches a real inbox.
      const { data: res } = await emailDeliveryAPI.sendTest('delivered@resend.dev');
      toast.success(res.sent ? `Test sent to ${res.to}` : 'Send failed — see the row below');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the test');
    } finally { setTesting(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await emailDeliveryAPI.list({
        status: status === 'all' ? undefined : status,
        search: search.trim() || undefined,
        days: 30,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email delivery');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); }, [status, canView]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="space-y-6" data-testid="email-delivery-page">
        <AccessState module="admin" action="view" items={[]} denied
          noAccessTitle="No access to Email Delivery"
          noAccessDescription="Email delivery records are administration-only. Ask an administrator if you need to see whether a message reached its recipient." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-delivery-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Email Delivery</h1>
          <p className="text-base text-fg-muted mt-1 max-w-2xl">
            Every email the app sends, and whether it reached the recipient. When one
            bounces, the person who triggered it is notified — automated sends go to{' '}
            {(data.fallback_recipients || []).join(', ') || 'the helpdesk'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" onClick={sendTest} disabled={testing}
              data-testid="email-delivery-send-test">
              <Send className="w-4 h-4 mr-2" />{testing ? 'Sending…' : 'Send test'}
            </Button>
          )}
          <Button variant="outline" onClick={load} disabled={loading} data-testid="email-delivery-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {!data.webhook_configured && (
        <Card className="border-amber-200 bg-amber-50/60" data-testid="email-delivery-not-configured">
          <CardContent className="p-4 flex items-start gap-3">
            <TriangleAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <b>Delivery tracking is not switched on yet.</b> Add a webhook in the Resend
              dashboard pointing at <code>{data.webhook_url || '/api/webhooks/resend'}</code>,
              subscribe it to the <i>bounced</i>, <i>complained</i>, <i>delivered</i> and{' '}
              <i>delivery delayed</i> events, then put its signing secret in the backend
              environment as <code>RESEND_WEBHOOK_SECRET</code>. Failures at the moment of
              sending are still recorded below without it.
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="failed" data-testid="email-tab-failed">
              Problems{data.failed_count ? ` (${data.failed_count})` : ''}
            </TabsTrigger>
            <TabsTrigger value="delivered" data-testid="email-tab-delivered">Delivered</TabsTrigger>
            <TabsTrigger value="opened" data-testid="email-tab-opened">
              Opened{data.opened_count ? ` (${data.opened_count})` : ''}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="email-tab-all">Everything</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
          <Input className="pl-9" placeholder="Recipient, subject or sender…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            data-testid="email-delivery-search" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-sm text-fg-muted">Loading…</p>
          ) : !data.messages.length ? (
            <div className="p-10 text-center" data-testid="email-delivery-empty">
              <MailWarning className="w-8 h-8 mx-auto text-fg-muted mb-3" />
              <p className="text-sm text-fg-muted">
                {status === 'failed'
                  ? 'No delivery problems in the last 30 days.'
                  : 'Nothing recorded in the last 30 days.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sent</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Triggered by</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Opened</TableHead>
                  <TableHead>What happened</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.messages.map((m) => (
                  <TableRow key={m.id} data-testid={`email-row-${m.id}`}>
                    <TableCell className="whitespace-nowrap text-sm">{when(m.sent_at)}</TableCell>
                    <TableCell className="text-sm">{(m.to || []).join(', ') || '—'}</TableCell>
                    <TableCell className="text-sm max-w-[320px] truncate" title={m.subject}>
                      {m.subject || '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {m.initiated_by_name || <span className="text-fg-muted">automated</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={TONE[m.status] || ''}>
                        {(m.status || 'unknown').replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap"
                      data-testid={`email-opened-${m.id}`}>
                      {m.open_count
                        ? <span className="text-emerald-700" title={`Last opened ${when(m.last_opened_at)}`}>
                            {m.open_count}&times; · {when(m.last_opened_at)}
                          </span>
                        : <span className="text-fg-muted">not yet</span>}
                    </TableCell>
                    <TableCell className="text-xs text-fg-muted max-w-[280px]" title={m.error || ''}>
                      {m.error || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
